import axios from 'axios';
import dayjs from 'dayjs';

/** Approvals › My Approvals: calls (API prefix inbox/), types and labels shared by the inbox pieces. */

export type Row = Record<string, any>;
export const PAGE = '/approvals/inbox';

const env = () => ({ platform: import.meta.env.APP_PLATFORM, source: import.meta.env.APP_SOURCE });

/** POST inbox/<path>; throws the server's message when status is false (the error keeps the result) */
export const call = async <T = any,>(path: string, body: object = {}): Promise<{ result: T; message: string }> => {
  const data = await axios
    .post(`inbox/${path}`, { ...env(), ...body })
    .then((r) => r.data)
    .catch((e) => {
      if (e?.response?.data?.message) return e.response.data;
      if (e?.message) return { status: false, message: e.message };
      throw e;
    });
  if (!data.status) {
    const err = new Error(data.message || 'Something went wrong. Nothing was changed.') as Error & { result?: unknown };
    err.result = data.result;
    throw err;
  }
  return { result: data.result as T, message: data.message };
};

export interface Head {
  docNo?: string;
  docDt?: string;
  party?: string;
  partyKind?: 'S' | 'C' | 'E' | null;
  partyName?: string;
  amount?: number | null;
  currency?: string;
  details?: string;
  txnType?: string;
}
export interface QueueRow {
  REQ_ID: number;
  COMP_CODE: string;
  DIVN_CODE: string;
  DIVN_NAME?: string;
  DOC_ID: number;
  MODULE: Module;
  TXN_CODE: string;
  TXN_NAME: string;
  STATUS: string;
  STEP: number;
  STEPS: number;
  FLOW_ID: number;
  CR_USER: string;
  CR_USER_NAME?: string;
  CR_DT: string;
  AGE_HOURS: number;
  LAST_REMARKS?: string;
  DELEGATOR?: string | null;
  DELEGATOR_NAME?: string | null;
  WAIT_GROUP?: string | null;
  HEAD: Head;
}
export interface Count {
  MODULE: Module;
  TXN_CODE: string;
  TXN_NAME: string;
  CNT: number;
  OLDEST_DT: string;
}
export type Module = 'HR' | 'FIN' | 'OP' | 'ASSET';

export const MODULES: { key: Module; label: string }[] = [
  { key: 'OP', label: 'Operations' },
  { key: 'FIN', label: 'Finance' },
  { key: 'HR', label: 'HR' },
  { key: 'ASSET', label: 'Assets' },
];

export const PARTY_LABEL: Record<string, string> = { S: 'Supplier', C: 'Customer', E: 'Employee' };

/** waiting time against the targets: under a day, up to three days, longer */
export const ageTone = (h: number) => (h < 24 ? 'ok' : h < 72 ? 'warn' : 'late');
export const ageText = (h: number | null | undefined) => {
  if (h === null || h === undefined) return '';
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 48) return `${Math.round(h)} h`;
  if (h < 24 * 60) return `${Math.round(h / 24)} d`;
  return `${Math.round(h / 24 / 30)} mo`;
};
export const TONE = {
  ok: { bg: '#dcfce7', fg: '#166534' },
  warn: { bg: '#fef3c7', fg: '#92400e' },
  late: { bg: '#fee2e2', fg: '#991b1b' },
};

const nf2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const money = (n: unknown) => (n === null || n === undefined || n === '' || isNaN(Number(n)) ? null : nf2.format(Number(n)));
export const when = (d: unknown) => (d ? dayjs(d as string).format('DD MMM YYYY, HH:mm') : '');

export const STATUS_LABEL: Record<string, { label: string; bg: string; fg: string }> = {
  P: { label: 'Waiting', bg: '#fef9c3', fg: '#854d0e' },
  V: { label: 'Sent back', bg: '#e0f2fe', fg: '#075985' },
  H: { label: 'On hold', bg: '#f1f5f9', fg: '#475569' },
  F: { label: 'Approved', bg: '#dcfce7', fg: '#166534' },
  A: { label: 'Approved', bg: '#dcfce7', fg: '#166534' },
  R: { label: 'Rejected', bg: '#fee2e2', fg: '#991b1b' },
  C: { label: 'Cancelled', bg: '#f1f5f9', fg: '#64748b' },
};

export const ACTIONS = {
  A: { label: 'Approve', past: 'Approved', reason: false },
  V: { label: 'Send back for changes', past: 'Sent back', reason: true },
  H: { label: 'Hold', past: 'Put on hold', reason: false },
  R: { label: 'Reject', past: 'Rejected', reason: true },
} as const;
export type ActionKey = keyof typeof ACTIONS;

/** credit limit requests are decided on the credit review, which shows the customer's risk */
export const CREDIT_TYPES = ['CRL-REQ', 'TCRL-REQ'];
export const creditLink = (txnType: string | undefined, docId: number) =>
  txnType === 'TCRL-REQ' ? `/finance/credit-limits/view/T/${docId}` : `/finance/credit-limits/view/P/${docId}`;
